/**
 * The file-transfer lifecycle HTTP client, shared by `apps/extension`, `apps/web` and
 * `apps/cli` (desktop slice 11f).
 *
 * WHAT THIS REPLACES. Nine functions, written THREE times. `client-core` already owned the
 * `TransferApi` interface the sender resume algorithm calls (`filetransfer/sendFile.ts`), the
 * `TransferApiError` class and its §4.4 discriminants, and every response validator
 * (`filetransfer/apiResponses.ts`) — the only piece still copied per host was the `fetch`
 * plumbing between them. `apps/cli/src/filetransfer/api.ts` said so in its own header
 * ("Mirrors `apps/web/src/api/client.ts`'s file-transfer section verbatim"), and the extension
 * and web copies were byte-identical to each other on eight of the nine functions.
 *
 * ⚠️ `cache: "no-store"` IS FORCED ON EVERY REQUEST, AND FOR `apps/web` AND `apps/cli` THAT IS
 * A BUG FIX, NOT A REFACTOR. Only the extension had it. It was added there after a hardware
 * failure in the offscreen receive host: Express ETags `GET /v1/transfers/:id`, so Chrome's
 * HTTP cache turned the reconcile loop's status reads conditional, the server answered
 * `304 Not Modified`, `304` fails `res.ok`, that classifies as retryable, and the resume loop
 * pinned in an infinite 35-second retry — server logs showed `OPTIONS 204 + GET 304` forever
 * with no download URL ever issued.
 *
 * `apps/web` wires the SAME shared resume loop (`filetransfer/senderTransfer.web.ts` →
 * `senderResume.ts`) against the same server from the same browser engine, so it carries the
 * identical latent bug — never observed there, but present. Adopting the extension's
 * semantics fixes it rather than changing it. Every endpoint here is a dynamic state read or
 * a one-shot presigned URL; none is ever validly cacheable, and the presigned R2 part bodies
 * additionally should not churn the HTTP cache. (Probed on both non-browser runtimes before
 * adopting it globally: Node accepts `no-store` — it names the value in the `TypeError` it
 * raises for an invalid one — and Bun ignores the field entirely, rejecting nothing. Harmless
 * on both.)
 *
 * ERROR CONTRACT, unchanged from all three hosts (docs/file-transfer-resume.md §4.4):
 *   - a non-2xx from a JSON endpoint  → `TransferApiError(status, body.error, body.message)`
 *   - a non-2xx from a presigned R2 URL (no JSON body) → `TransferApiError(status, "presigned", …)`
 *   - `fetch` itself rejecting (network I/O, not a response) → `TransferApiError(0, "network", …)`
 *
 * This deliberately does NOT route through `createApiClient`. That client owns `ApiError`, a
 * different taxonomy the resume algorithm cannot discriminate on, and it reads whole bodies as
 * text — which `fetchTransferPartStream` must never do, since a part is several MiB and is
 * consumed as a stream.
 */

import {
  type CreateTransferResponse,
  parseCreateTransferResponse,
  parseTransferStatusResponse,
  parseTransferUrlResponse,
  readTransferJson,
  type TransferStatusResponse,
  type TransferUploadUrlResponse,
} from "../filetransfer/apiResponses";
import { TransferApiError } from "../filetransfer/errors";

/**
 * Hand-declared and deliberately minimal — NOT `typeof globalThis.fetch`, for the reason
 * `api/client.ts`'s `ApiFetch` spells out: inside this package that alias resolves to lib.dom's
 * fetch and inside the CLI / `client-node` / the Electron main process it resolves to
 * undici's, and the two are structurally different, so the seam would fail to typecheck on a
 * runtime it exists to serve.
 *
 * Distinct from `ApiFetch` rather than reusing it, because these calls need two things the
 * JSON client does not: `cache` (see the header) and `body` as a stream (a part response is
 * never buffered).
 */
export type TransferFetch = (
  input: string,
  init?: {
    method?: string;
    headers?: Record<string, string>;
    body?: string | Uint8Array;
    signal?: AbortSignal;
    cache?: "no-store";
  }
) => Promise<{
  ok: boolean;
  status: number;
  json(): Promise<unknown>;
  body: ReadableStream<Uint8Array> | null;
}>;

export interface TransferApiDeps {
  /**
   * Origin AND version prefix, e.g. `https://sync.reflecto.dev/v1` — the paths below append
   * directly to it. This is `API_BASE` on both browser hosts; the CLI derives the same string
   * from `SERVER_URL`.
   */
  apiBase: string;
  /** Injectable so this package's own tests exercise the real handling without a network. */
  fetch?: TransferFetch;
}

/**
 * Every relay-path call, sender and receiver side. `token` is per-call rather than a client-
 * level dep because that is how all three hosts already thread it — the callers hold the
 * token, and a `getToken` seam here would be a re-plumb disguised as a move.
 */
export interface TransferHttpApi {
  createTransfer(
    token: string,
    args: {
      transferId: string;
      recipientDeviceId: string;
      partCount: number;
      cipherBytes: number;
    }
  ): Promise<CreateTransferResponse>;
  getTransfer(token: string, transferId: string): Promise<TransferStatusResponse>;
  getTransferUploadUrl(
    token: string,
    transferId: string,
    partIndex: number
  ): Promise<TransferUploadUrlResponse>;
  putTransferPart(url: string, body: Uint8Array, signal?: AbortSignal): Promise<void>;
  getTransferDownloadUrl(
    token: string,
    transferId: string,
    partIndex: number
  ): Promise<TransferUploadUrlResponse>;
  fetchTransferPartStream(url: string, signal?: AbortSignal): Promise<ReadableStream<Uint8Array>>;
  markTransferPartUploaded(token: string, transferId: string, partIndex: number): Promise<void>;
  completeTransfer(token: string, transferId: string): Promise<void>;
  deleteTransfer(token: string, transferId: string): Promise<void>;
}

export function createTransferApi(deps: TransferApiDeps): TransferHttpApi {
  const doFetch: TransferFetch = deps.fetch ?? ((input, init) => fetch(input, init as RequestInit));

  /** `fetch` itself rejecting — network I/O, not an HTTP response — maps onto §4.4's `network`. */
  async function transferFetch(
    url: string,
    init?: Parameters<TransferFetch>[1]
  ): Promise<Awaited<ReturnType<TransferFetch>>> {
    try {
      return await doFetch(url, { ...init, cache: "no-store" });
    } catch (err) {
      const message = err instanceof Error ? err.message : "network request failed";
      throw new TransferApiError(0, "network", message);
    }
  }

  function auth(token: string): Record<string, string> {
    return { Authorization: `Bearer ${token}` };
  }

  function path(transferId: string, suffix = ""): string {
    return `${deps.apiBase}/transfers/${encodeURIComponent(transferId)}${suffix}`;
  }

  /**
   * Maps a non-2xx JSON-endpoint response onto `TransferApiError` per §4.4. `readTransferJson`
   * yields `undefined` rather than throwing on a non-JSON or empty body, which is common on
   * 4xx/5xx — so a garbage error body degrades to `code: "unknown"`, never to a stray
   * `SyntaxError` escaping the taxonomy.
   */
  async function throwJsonTransferError(res: Awaited<ReturnType<TransferFetch>>): Promise<never> {
    const parsed = await readTransferJson(res);
    const body = parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : {};
    const code = typeof body.error === "string" ? body.error : "unknown";
    const message = typeof body.message === "string" ? body.message : "";
    throw new TransferApiError(res.status, code, message);
  }

  /** Maps a non-2xx from a presigned R2 URL (no JSON body) onto `TransferApiError` per §4.4. */
  function throwPresignedTransferError(res: { status: number }): never {
    throw new TransferApiError(res.status, "presigned", `presigned request failed (${res.status})`);
  }

  return {
    /** POST /v1/transfers — register a transfer before uploading any parts. */
    async createTransfer(token, args) {
      const res = await transferFetch(`${deps.apiBase}/transfers`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...auth(token) },
        body: JSON.stringify(args),
      });
      if (!res.ok) await throwJsonTransferError(res);
      return parseCreateTransferResponse(await readTransferJson(res), res.status);
    },

    /** GET /v1/transfers/:id — sender or recipient; the resume algorithm's `GET` step (§5.2/§6.2). */
    async getTransfer(token, transferId) {
      const res = await transferFetch(path(transferId), { headers: auth(token) });
      if (!res.ok) await throwJsonTransferError(res);
      return parseTransferStatusResponse(await readTransferJson(res), res.status);
    },

    /** GET /v1/transfers/:id/parts/:n/upload-url — a fresh presigned PUT URL for one part. */
    async getTransferUploadUrl(token, transferId, partIndex) {
      const res = await transferFetch(path(transferId, `/parts/${partIndex}/upload-url`), {
        headers: auth(token),
      });
      if (!res.ok) await throwJsonTransferError(res);
      return parseTransferUrlResponse(
        await readTransferJson(res),
        res.status,
        "GET .../upload-url"
      );
    },

    /**
     * PUT the encrypted part bytes straight to the presigned URL. Deliberately NOT
     * authenticated — the presigned URL carries its own signature, exactly like any S3/R2
     * presigned upload. Never runs in a service worker; a part can be several MiB.
     */
    async putTransferPart(url, body, signal) {
      const res = await transferFetch(url, { method: "PUT", body, signal });
      if (!res.ok) throwPresignedTransferError(res);
    },

    /** GET /v1/transfers/:id/parts/:n/download-url — a fresh presigned GET URL (recipient side). */
    async getTransferDownloadUrl(token, transferId, partIndex) {
      const res = await transferFetch(path(transferId, `/parts/${partIndex}/download-url`), {
        headers: auth(token),
      });
      if (!res.ok) await throwJsonTransferError(res);
      return parseTransferUrlResponse(
        await readTransferJson(res),
        res.status,
        "GET .../download-url"
      );
    },

    /**
     * Open a presigned download URL as a ciphertext `ReadableStream` — never buffers the whole
     * part body (the receive loop streams and slices it via `ciphertextSplitter.ts`).
     * Unauthenticated, same presigned contract as `putTransferPart`.
     *
     * `signal` was the extension's alone and is now available to every host: its offscreen
     * receive wiring (`receiveDeps.ext.ts`) passes a `PART_DOWNLOAD_TIMEOUT_MS`-bound
     * `AbortSignal.timeout(...)` so a stalled R2 response — dead TCP, or a body stream that
     * never emits — cannot hang `reader.read()` forever. Aborting after the response has begun
     * streaming rejects in-flight reads on `res.body` too, not merely the initial fetch.
     */
    async fetchTransferPartStream(url, signal) {
      const res = await transferFetch(url, signal ? { signal } : undefined);
      if (!res.ok) throwPresignedTransferError(res);
      if (!res.body) {
        throw new Error("GET (presigned part download): response had no body");
      }
      return res.body;
    },

    /** POST /v1/transfers/:id/parts/:n/uploaded — confirm one part landed at the blob store. */
    async markTransferPartUploaded(token, transferId, partIndex) {
      const res = await transferFetch(path(transferId, `/parts/${partIndex}/uploaded`), {
        method: "POST",
        headers: auth(token),
      });
      if (!res.ok) await throwJsonTransferError(res);
    },

    /** POST /v1/transfers/:id/complete — every part uploaded; finalize. */
    async completeTransfer(token, transferId) {
      const res = await transferFetch(path(transferId, "/complete"), {
        method: "POST",
        headers: auth(token),
      });
      if (!res.ok) await throwJsonTransferError(res);
    },

    /**
     * DELETE /v1/transfers/:id — abort (offer declined mid-upload, or retries exhausted).
     *
     * A 404 THROWS here, matching the browser hosts. It is not an oversight to fix by
     * swallowing it: `isDefinitive404` is a named §4.4 discriminant the resume algorithm
     * branches on, so a 404 is information. The CLI alone treated a delete-404 as success;
     * its wrapper keeps doing so locally rather than imposing that on every host.
     */
    async deleteTransfer(token, transferId) {
      const res = await transferFetch(path(transferId), {
        method: "DELETE",
        headers: auth(token),
      });
      if (!res.ok) await throwJsonTransferError(res);
    },
  };
}
